import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import api from '@/lib/api';
import {
  BookOpen, Plus, Save, Trash2, Upload, Download, Check, Loader2,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORIES = ['general', 'intake', 'backlog', 'prioritization', 'planning', 'communication', 'ops'];

const CATEGORY_COLORS = {
  general: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  intake: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  backlog: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  prioritization: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  planning: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  communication: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  ops: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
};

// ---------------------------------------------------------------------------
// Tiny markdown renderer — emits React elements (safe, no innerHTML).
// Handles headings, bold, italic, inline code, fenced code, and bullet lists.
// ---------------------------------------------------------------------------

function renderInline(text, keyPrefix) {
  // Tokenize inline markdown into React nodes. Order matters: code -> bold -> italic.
  const nodes = [];
  let remaining = text;
  let i = 0;
  const patterns = [
    { re: /`([^`]+)`/, type: 'code' },
    { re: /\*\*([^*]+)\*\*/, type: 'bold' },
    { re: /\*([^*\n]+)\*/, type: 'italic' },
  ];

  while (remaining.length > 0) {
    let earliest = null;
    for (const p of patterns) {
      const m = remaining.match(p.re);
      if (m && (earliest === null || m.index < earliest.match.index)) {
        earliest = { match: m, type: p.type };
      }
    }
    if (!earliest) {
      nodes.push(<Fragment key={`${keyPrefix}-t-${i}`}>{remaining}</Fragment>);
      break;
    }
    const { match, type } = earliest;
    if (match.index > 0) {
      nodes.push(<Fragment key={`${keyPrefix}-t-${i}`}>{remaining.slice(0, match.index)}</Fragment>);
      i += 1;
    }
    if (type === 'code') {
      nodes.push(
        <code key={`${keyPrefix}-c-${i}`} className="bg-gray-100 dark:bg-gray-800 px-1 rounded text-xs">
          {match[1]}
        </code>
      );
    } else if (type === 'bold') {
      nodes.push(<strong key={`${keyPrefix}-b-${i}`}>{match[1]}</strong>);
    } else if (type === 'italic') {
      nodes.push(<em key={`${keyPrefix}-i-${i}`}>{match[1]}</em>);
    }
    remaining = remaining.slice(match.index + match[0].length);
    i += 1;
  }
  return nodes;
}

function MarkdownPreview({ source }) {
  if (!source) {
    return <p className="text-sm text-gray-400">Nothing to preview yet.</p>;
  }

  const lines = source.split('\n');
  const blocks = [];
  let inCode = false;
  let codeBuf = [];
  let listBuf = [];
  let blockIdx = 0;

  const flushList = () => {
    if (listBuf.length === 0) return;
    blocks.push(
      <ul key={`ul-${blockIdx++}`} className="list-disc pl-5 my-2 text-sm text-gray-700 dark:text-gray-300 space-y-1">
        {listBuf.map((item, idx) => (
          <li key={idx}>{renderInline(item, `ul-${blockIdx}-${idx}`)}</li>
        ))}
      </ul>
    );
    listBuf = [];
  };

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    if (line.trim().startsWith('```')) {
      if (inCode) {
        blocks.push(
          <pre key={`pre-${blockIdx++}`} className="bg-gray-100 dark:bg-gray-800 rounded p-2 my-2 overflow-x-auto text-xs">
            <code>{codeBuf.join('\n')}</code>
          </pre>
        );
        codeBuf = [];
        inCode = false;
      } else {
        flushList();
        inCode = true;
      }
      continue;
    }
    if (inCode) { codeBuf.push(line); continue; }

    if (/^###\s+/.test(line)) {
      flushList();
      blocks.push(
        <h3 key={`h3-${blockIdx++}`} className="text-base font-semibold mt-3 mb-1 text-gray-900 dark:text-gray-100">
          {renderInline(line.replace(/^###\s+/, ''), `h3-${blockIdx}`)}
        </h3>
      );
      continue;
    }
    if (/^##\s+/.test(line)) {
      flushList();
      blocks.push(
        <h2 key={`h2-${blockIdx++}`} className="text-lg font-semibold mt-4 mb-2 text-gray-900 dark:text-gray-100">
          {renderInline(line.replace(/^##\s+/, ''), `h2-${blockIdx}`)}
        </h2>
      );
      continue;
    }
    if (/^#\s+/.test(line)) {
      flushList();
      blocks.push(
        <h1 key={`h1-${blockIdx++}`} className="text-xl font-bold mt-4 mb-2 text-gray-900 dark:text-gray-100">
          {renderInline(line.replace(/^#\s+/, ''), `h1-${blockIdx}`)}
        </h1>
      );
      continue;
    }
    if (/^\s*-\s+/.test(line)) {
      listBuf.push(line.replace(/^\s*-\s+/, ''));
      continue;
    }
    if (line.trim() === '') {
      flushList();
      continue;
    }
    flushList();
    blocks.push(
      <p key={`p-${blockIdx++}`} className="text-sm text-gray-700 dark:text-gray-300 my-2">
        {renderInline(line, `p-${blockIdx}`)}
      </p>
    );
  }
  flushList();
  if (inCode && codeBuf.length > 0) {
    blocks.push(
      <pre key={`pre-${blockIdx++}`} className="bg-gray-100 dark:bg-gray-800 rounded p-2 my-2 overflow-x-auto text-xs">
        <code>{codeBuf.join('\n')}</code>
      </pre>
    );
  }
  return <>{blocks}</>;
}

// ---------------------------------------------------------------------------
// Frontmatter helpers — preview-only stringifier so users see what exports.
// ---------------------------------------------------------------------------

function buildFrontmatter(skill) {
  const agents = (skill.agent_ids || []).map((a) => `  - ${a}`).join('\n');
  const lines = [
    '---',
    `name: ${skill.name || ''}`,
    `category: ${skill.category || 'general'}`,
    'agent_ids:',
    agents || '  []',
    `active: ${skill.active !== false}`,
    `version: ${skill.version || 1}`,
    '---',
    '',
  ];
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const EMPTY_SKILL = {
  name: '',
  category: 'general',
  agent_ids: [],
  active: true,
  version: 1,
  body: '## When to use\n\n\n\n## Behavior\n\n\n\n## Examples\n\n',
  is_default: false,
};

export default function AgentSkills() {
  const [skills, setSkills] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get('/agent-skills/').then((r) => r.data),
      api.get('/agents/').then((r) => r.data).catch(() => []),
    ])
      .then(([skillsRes, agentsRes]) => {
        setSkills(skillsRes);
        setAgents(agentsRes);
        if (skillsRes.length > 0) {
          setSelectedId(skillsRes[0].id);
          setDraft(skillsRes[0]);
        }
      })
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  const selectSkill = (id) => {
    const found = skills.find((s) => s.id === id);
    if (found) {
      setSelectedId(id);
      setDraft(found);
      setError('');
    }
  };

  const startNew = () => {
    setSelectedId(null);
    setDraft({ ...EMPTY_SKILL, name: 'New Skill' });
    setError('');
  };

  const toggleAgent = (agentId) => {
    if (!draft) return;
    const isAttached = (draft.agent_ids || []).includes(agentId);
    const next = isAttached
      ? draft.agent_ids.filter((a) => a !== agentId)
      : [...(draft.agent_ids || []), agentId];
    setDraft({ ...draft, agent_ids: next });
  };

  const onSave = async () => {
    if (!draft || !draft.name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      let saved;
      if (selectedId) {
        const res = await api.patch(`/agent-skills/${selectedId}`, draft);
        saved = res.data;
        setSkills((prev) => prev.map((s) => (s.id === saved.id ? saved : s)));
      } else {
        const res = await api.post('/agent-skills/', draft);
        saved = res.data;
        setSkills((prev) => [saved, ...prev]);
        setSelectedId(saved.id);
      }
      setDraft(saved);
      setToast('Skill saved');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!selectedId || !draft) return;
    if (draft.is_default) {
      setError('Default skills cannot be deleted.');
      return;
    }
    if (!window.confirm(`Delete skill "${draft.name}"?`)) return;
    try {
      await api.delete(`/agent-skills/${selectedId}`);
      setSkills((prev) => prev.filter((s) => s.id !== selectedId));
      setSelectedId(null);
      setDraft(null);
      setToast('Skill deleted');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const onImportClick = () => fileInputRef.current?.click();

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset so the same file can be picked again
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await api.post('/agent-skills/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const created = res.data;
      setSkills((prev) => [created, ...prev]);
      setSelectedId(created.id);
      setDraft(created);
      setToast('Skill imported');
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const onExport = async () => {
    if (!selectedId) return;
    try {
      const res = await api.get(`/agent-skills/${selectedId}/export`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const slug = (draft.name || 'skill').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      a.href = url;
      a.download = `${slug || 'skill'}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const frontmatterHeader = useMemo(() => (draft ? buildFrontmatter(draft) : ''), [draft]);

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-600 dark:text-gray-400">
        <Loader2 className="animate-spin" size={16} /> Loading skills...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BookOpen className="text-[#98139C]" size={28} />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Agent Skills</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Markdown-first prompt fragments attached to one or more agents.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar */}
        <aside className="col-span-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="p-3 border-b border-gray-200 dark:border-gray-800 flex gap-2">
            <button
              onClick={startNew}
              className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-md text-sm font-medium"
            >
              <Plus size={14} /> New skill
            </button>
            <button
              onClick={onImportClick}
              className="inline-flex items-center justify-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
              title="Import .md file"
            >
              <Upload size={14} /> Import .md
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".md,text/markdown"
              onChange={onImportFile}
              className="hidden"
            />
          </div>
          <ul className="max-h-[70vh] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
            {skills.length === 0 && (
              <li className="p-4 text-sm text-gray-500 dark:text-gray-400">No skills yet.</li>
            )}
            {skills.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => selectSkill(s.id)}
                  className={`w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800 ${selectedId === s.id ? 'bg-[#f0dcf0] dark:bg-[#98139C]/20' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{s.name}</span>
                    {!s.active && (
                      <span className="text-[10px] uppercase text-gray-400 ml-2">off</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${CATEGORY_COLORS[s.category] || CATEGORY_COLORS.general}`}>
                      {s.category}
                    </span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      {(s.agent_ids || []).length} agent{(s.agent_ids || []).length === 1 ? '' : 's'}
                    </span>
                    {s.is_default && (
                      <span className="text-[10px] text-amber-600 dark:text-amber-400">default</span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Editor */}
        <main className="col-span-9 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
          {!draft ? (
            <div className="p-12 text-center text-sm text-gray-500 dark:text-gray-400">
              Select a skill on the left, or create a new one.
            </div>
          ) : (
            <div className="p-6">
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-4">
                <input
                  type="text"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Skill name"
                  className="text-lg font-semibold bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-[#98139C] outline-none text-gray-900 dark:text-gray-100 flex-1 mr-4"
                />
                <div className="flex items-center gap-2">
                  {selectedId && (
                    <button
                      onClick={onExport}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                      title="Export as .md"
                    >
                      <Download size={14} /> Export
                    </button>
                  )}
                  {selectedId && !draft.is_default && (
                    <button
                      onClick={onDelete}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 rounded-md text-sm hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  )}
                  <button
                    onClick={onSave}
                    disabled={saving}
                    className="inline-flex items-center gap-1 px-4 py-1.5 bg-[#98139C] hover:bg-[#7a0f7d] disabled:bg-[#98139C]/40 text-white rounded-md text-sm font-medium"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save
                  </button>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded text-sm">
                  {error}
                </div>
              )}

              {/* Metadata controls */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium uppercase text-gray-500 dark:text-gray-400 mb-1">
                    Category
                  </label>
                  <select
                    value={draft.category}
                    onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded text-sm"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase text-gray-500 dark:text-gray-400 mb-1">
                    Version
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={draft.version}
                    onChange={(e) => setDraft({ ...draft, version: parseInt(e.target.value, 10) || 1 })}
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={draft.active}
                      onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                      className="rounded"
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs font-medium uppercase text-gray-500 dark:text-gray-400 mb-1">
                  Attach to agents
                </label>
                <div className="flex flex-wrap gap-2">
                  {agents.length === 0 && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">No agents available.</span>
                  )}
                  {agents.map((agent) => {
                    const isAttached = (draft.agent_ids || []).includes(agent.id);
                    return (
                      <button
                        key={agent.id}
                        onClick={() => toggleAgent(agent.id)}
                        aria-pressed={isAttached}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border ${
                          isAttached
                            ? 'bg-[#f0dcf0] dark:bg-[#98139C]/20 border-[#98139C] text-[#4F0077] dark:text-[#d87bdb]'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {isAttached && <Check size={12} />}
                        {agent.name || agent.id}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Split editor / preview */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium uppercase text-gray-500 dark:text-gray-400 mb-1">
                    Markdown (frontmatter + body)
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-950 border border-gray-300 dark:border-gray-700 rounded-md overflow-hidden">
                    <pre className="bg-gray-100 dark:bg-gray-900 text-xs px-3 py-2 text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800 whitespace-pre-wrap">
                      {frontmatterHeader}
                    </pre>
                    <textarea
                      value={draft.body || ''}
                      onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                      className="w-full px-3 py-2 bg-transparent text-sm font-mono text-gray-900 dark:text-gray-100 outline-none resize-none"
                      rows={20}
                      spellCheck={false}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase text-gray-500 dark:text-gray-400 mb-1">
                    Preview
                  </label>
                  <div className="border border-gray-300 dark:border-gray-700 rounded-md p-4 min-h-[400px] bg-white dark:bg-gray-950 overflow-y-auto max-h-[600px]">
                    <MarkdownPreview source={draft.body || ''} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Toast — B-8: aria-live for screen readers */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 right-6 bg-emerald-600 text-white rounded-lg px-4 py-2 shadow-lg text-sm flex items-center gap-2"
        >
          <Check size={14} /> {toast}
        </div>
      )}
    </div>
  );
}
